import type { OrderStatus } from '@/api/types';

/**
 * Status colours from spec §13: green SUCCEEDED, red FAILED, amber UNKNOWN, grey REVERSED,
 * purple RESERVED, and NEEDS_MANUAL_REVIEW pulsing so it cannot be scrolled past.
 */
const STYLES: Record<OrderStatus, string> = {
  SUCCEEDED: 'bg-emerald-100 text-emerald-800 ring-emerald-600/20',
  FAILED: 'bg-red-100 text-red-800 ring-red-600/20',
  UNKNOWN: 'bg-amber-100 text-amber-900 ring-amber-600/30',
  REVERSED: 'bg-ink-100 text-ink-700 ring-ink-500/20',
  RESERVED: 'bg-violet-100 text-violet-800 ring-violet-600/20',
  RESERVATION_CANCELLED: 'bg-ink-100 text-ink-600 ring-ink-400/20',
  NEEDS_MANUAL_REVIEW: 'bg-red-600 text-white ring-red-900/30 animate-attention',
  SUBMITTED: 'bg-sky-100 text-sky-800 ring-sky-600/20',
  CREATED: 'bg-ink-100 text-ink-600 ring-ink-400/20',
};

const LABELS: Partial<Record<OrderStatus, string>> = {
  NEEDS_MANUAL_REVIEW: 'NEEDS REVIEW',
  RESERVATION_CANCELLED: 'CANCELLED',
};

export function StatusPill({ status }: { status: OrderStatus }) {
  return (
    <span
      className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${STYLES[status]}`}
    >
      {LABELS[status] ?? status}
    </span>
  );
}

export function SeverityPill({ severity }: { severity: 'P1' | 'P2' | 'P3' }) {
  const styles = {
    P1: 'bg-red-600 text-white',
    P2: 'bg-amber-500 text-white',
    P3: 'bg-ink-400 text-white',
  } as const;
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-bold ${styles[severity]}`}>
      {severity}
    </span>
  );
}

export function LevelPill({ level }: { level: 'OK' | 'WARN' | 'CRITICAL' }) {
  const styles = {
    OK: 'bg-emerald-100 text-emerald-800',
    WARN: 'bg-amber-100 text-amber-900',
    CRITICAL: 'bg-red-600 text-white animate-attention',
  } as const;
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${styles[level]}`}>
      {level}
    </span>
  );
}
